import React from 'react';
import styles from './brickItemsPanel.scss';
import { inject, observer } from 'mobx-react';
import { ArticleState, IVariantStore } from '../../../types';
import VariantButtonList from './variantButtonList/variantButtonList';
import TabNavigationHelper from '../../../helpers/tabNavigationHelper';
import classNames from 'classnames';
import { IBrickItem } from '../../../stores/types';
import { decodeHTML, decodeRtfText } from '../../../helpers/html-helper';

const BrickItemsPanel = inject('variantStore')(
    observer(({ variantStore }: IVariantStore) => {
        const tabNavigation: TabNavigationHelper = TabNavigationHelper.instance;

        function handleCheckBoxClick(item: IBrickItem, openDMAfterLoad: boolean, index: number): void {
            variantStore?.setBrickItemPosition(index); // for piwik
            // No need to do trigger variant change if this is already selected
            // Prevents unnecessary state changes and backend dataRefresh calls
            if (item && !item.isBrickItemSelected) {
                variantStore?.brickItemSelectionChanged(item, openDMAfterLoad);
                variantStore?.mainStore.trackVisitedArticleOnVariantChange();
            }
        }

        function handleDetailsButtonClick(
            event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
            item: IBrickItem,
            index: number
        ): void {
            if (item.isBrickItemSelected) {
                event.stopPropagation();
                variantStore?.mainStore.productInfoStore.setItemOrigin('FlexSets-1');
                variantStore?.setDetailsModalOpen(true);
            } else {
                handleCheckBoxClick(item, true, index);
            }
        }

        return (<div data-testid='brickItemsPanel' data-semantic-id='brickItemsPanel' className={styles.brick_items_panel}>
            {variantStore?.getSortedBrickItems.map((item, index) => {
                const title = item.includedQuantity > 1 ? item.includedQuantity + 'x ' + item.salesDesignation
                    : item.salesDesignation;
                const isGratis = item.isFreeInSet;
                const articleObsolete = item.articleState === ArticleState.Obsoleted;

                return (
                    <div key={`${index + 1}-${item.brickNo}-${item.brickItemNo}`}
                        data-testid='flexset-module-layer_1' data-semantic-id='flexset-module-layer_1'
                        data-flexset-id={item.fsCode}
                        data-flexset-name={variantStore?.mainStore.dataStore.orderModule.flexSetInfo.displayName}
                        data-flexset-module-layer_0-index={variantStore?.getOMBrickPosition}
                        data-flexset-module-layer_1-category={item.mainNavigationKeyPath}
                        data-flexset-module-layer_1-index={index} // position of article within the brick
                        data-flexset-item-name={item.salesDesignation}
                        //SalesArticleNo
                        data-flexset-item-sku={variantStore?.mainStore.getSalesArticleNo(item.selectedSavKey)}
                        className={classNames(styles.brick_item,
                            articleObsolete ? styles.disabled : '',
                            {
                                [styles.bi_selected]: item.isBrickItemSelected
                            }
                        )} data-sortedbrickitem={item.brickItemNo}
                        onClick={() => handleCheckBoxClick(item, false, index)}
                        ref={(el) => {
                            tabNavigation.setFocusAtt(el);
                            tabNavigation.toggleDisabledAtt(el, articleObsolete)
                        }}>
                        {articleObsolete &&
                            <span className={styles.not_available_txt}>
                                {decodeRtfText(variantStore.mainStore.localization.generalL10n.currentlyNotAvailable)}
                            </span>
                        }
                        <div className={styles.b_short_info}>
                            <span className={classNames(styles.checkmark,
                                { [styles.bi_selected]: item.isBrickItemSelected }
                            )}>
                                <input type='checkbox'
                                    data-brickitemno={item.brickItemNo}
                                    data-brickno={item.brickNo}
                                    data-masterarticleno={item.masterArticleNo}
                                    className={styles.b_checkbox}
                                    checked={item.isBrickItemSelected}
                                    readOnly={true}
                                />
                                <div className={styles.checkmark_stem}></div>
                                <div className={styles.checkmark_kick}></div>
                            </span>

                            <div className={styles.brick_image}>
                                <img src={item.imageFullPath} alt={item.salesDesignation} />
                            </div>
                            <div className={styles.sales_designation}>
                                <span className={styles.brick_name}>
                                    {
                                        isGratis && !articleObsolete &&
                                        <div className={styles.free_in_set}>
                                            {decodeHTML(variantStore?.mainStore.localization.generalL10n.gratis)}
                                        </div>
                                    }
                                    {title}
                                </span>
                                {!articleObsolete &&
                                    <button className={classNames(styles.details_btn, {
                                        [styles.disabled]: !item.isBrickItemSelected
                                    })}
                                        data-testid='flexset-button-layer_1-details'
                                        data-semantic-id='flexset-button-layer_1-details'
                                        data-flexset-id={item.fsCode}
                                        data-flexset-name={variantStore?.mainStore.dataStore.orderModule.flexSetInfo.
                                            displayName}
                                        data-flexset-module-layer_0-index={variantStore?.getOMBrickPosition}
                                        data-flexset-module-layer_1-category={item.mainNavigationKeyPath}
                                        data-flexset-module-layer_1-index={index} // position of article within the brick
                                        data-flexset-item-name={item.salesDesignation}
                                        //SalesArticleNo
                                        data-flexset-item-sku={variantStore?.mainStore.
                                            getSalesArticleNo(item.selectedSavKey)}
                                        onClick={(event) => handleDetailsButtonClick(event, item, index)}
                                        ref={(el) => {
                                            tabNavigation.setFocusAtt(el);
                                        }}>
                                        <span className={styles.d_btn_text}>
                                            {decodeHTML(variantStore.mainStore.localization.generalL10n.details)}
                                        </span>
                                    </button>
                                }
                            </div>
                        </div>
                        <div className={classNames(styles.b_variant, {
                            [styles.show_vbl]: item.isBrickItemSelected
                                && (item.variantSelection.showColorSwitcher
                                    || item.variantSelection.showModelSwitcher
                                    || item.variantSelection.showSizeSwitcher
                                    || (
                                        item.variantSelection.availableModels?.length &&
                                        item.variantSelection.availableModels[0]?.code !== 0
                                    )
                                )
                        })}>
                            <div className={styles.inner}>
                                <VariantButtonList brickItem={item} />
                            </div>
                        </div>
                    </div>
                );
            })}
        </div >
        );
    }));

export default BrickItemsPanel;

